import React, { useState, useEffect } from 'react';
import { Box, List, ListItemText, Checkbox, TableBody, TableCell, Table, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  MainContainer,
  DescriptionText,
  LayoutGrid,
  RolesPanel,
  RolesTitle,
  RoleItem,
  MappingPanel,
  MappingTitle,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  SaveButton
} from './styles';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import {
  searchRoles,
  searchDocumentTypes,
  createRoleDocumentMapping,
  getRoleDocumentMapping
} from '../../../modules/masterdata/actions';
import {
  selectRolesList,
  selectDocumentTypesList,
  selectRoleDocumentMappingData,
  selectCreateRoleDocumentMappingSuccess,
  selectCreateRoleDocumentMappingLoading,
  selectRoleDocumentMappingLoading,
  selectCreateRoleDocumentMappingError,
  selectCreateRoleDocumentMappingMessage
} from '../../../modules/masterdata/selectors';
import { AppDispatch } from '../../../store';
import { resetCreateRoleDocumentMappingState } from '../../../modules/masterdata/reducers';
import AlertPopup from '../../../components/common/AlertPopup';

const RoleDocumentTypeMapping: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  // Redux Selectors
  const roles = useSelector(selectRolesList);
  const documentTypes = useSelector(selectDocumentTypesList);
  const currentMapping = useSelector(selectRoleDocumentMappingData);
  const isSaving = useSelector(selectCreateRoleDocumentMappingLoading);
  const saveSuccess = useSelector(selectCreateRoleDocumentMappingSuccess);
  const saveError = useSelector(selectCreateRoleDocumentMappingError);
  const saveMessage = useSelector(selectCreateRoleDocumentMappingMessage);
  const isLoadingMapping = useSelector(selectRoleDocumentMappingLoading);

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('role-doc-mapping');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Role Document Mapping', path: '/master-data/role-doc-mapping' },
    ]);
    setIsShow(true);

    // Initial Fetch
    dispatch(searchRoles({ search: '', page: 1, page_size: 100 }));
    dispatch(searchDocumentTypes({ search: '', page: 1, page_size: 100 }));

    return () => {
      dispatch(resetCreateRoleDocumentMappingState());
    }
  }, [setActivePage, setPath, setIsShow, dispatch]);

  // Set default selected role when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && selectedRoleId === null) {
      setSelectedRoleId(roles[0].role_id);
    }
  }, [roles, selectedRoleId]);

  // Fetch mapping when selected role changes
  useEffect(() => {
    if (selectedRoleId) {
      dispatch(getRoleDocumentMapping(selectedRoleId));
    }
  }, [selectedRoleId, dispatch]);

  // Update local state when mapping data comes from Redux
  useEffect(() => {
    if (currentMapping) {
      // Check if the mapping corresponds to the currently selected role to avoid race conditions
      // Using loose equality to handle potential string/number mismatch
      // eslint-disable-next-line eqeqeq
      if (currentMapping.role_id == selectedRoleId) {
        const docTypes = currentMapping.document_types || [];
        // Handle case where document_types might be objects (backend inconsistency protection)
        const ids = docTypes.map((d: any) => {
          if (typeof d === 'object' && d !== null) {
            return d.document_id || d.id;
          }
          return d;
        });
        setSelectedDocIds(ids);
      }
    } else {
      // Handle case where mapping might be empty or loading
    }
  }, [currentMapping, selectedRoleId]);


  const handleRoleClick = (id: number) => {
    setSelectedRoleId(id);
    setSelectedDocIds([]); // Clear while loading new ones
  };

  const handleToggleDoc = (docId: number) => {
    setSelectedDocIds(prev => {
      const isSelected = prev.includes(docId);
      if (isSelected) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const handleSave = () => {
    if (selectedRoleId) {
      dispatch(createRoleDocumentMapping({
        role_id: selectedRoleId,
        document_types: selectedDocIds
      }));
    }
  };

  useEffect(() => {
    if (saveSuccess) {
      setAlert({ message: saveMessage || 'Role document mapping updated successfully', severity: 'success' });
      dispatch(resetCreateRoleDocumentMappingState());
      if (selectedRoleId) dispatch(getRoleDocumentMapping(selectedRoleId));
    }
    if (saveError) {
      setAlert({ message: saveError || 'Failed to update mapping', severity: 'error' });
      dispatch(resetCreateRoleDocumentMappingState());
    }
  }, [saveSuccess, saveError, saveMessage, dispatch, selectedRoleId]);


  return (
    <MainContainer>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <DescriptionText>
        Set upload rules by selecting a role and assigning the required documents.
      </DescriptionText>

      <LayoutGrid>
        {/* Left Panel: Roles List */}
        <RolesPanel>
          <RolesTitle>Roles</RolesTitle>
          <List component="nav" disablePadding>
            {roles.map((role) => (
              <RoleItem
                key={role.role_id}
                selected={selectedRoleId === role.role_id}
                onClick={() => handleRoleClick(role.role_id)}
              >
                <ListItemText primary={role.role_name} />
              </RoleItem>
            ))}
          </List>
        </RolesPanel>

        {/* Right Panel: Document Mapping */}
        <MappingPanel>
          <MappingTitle>
            Document Types Required
            {isLoadingMapping && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </MappingTitle>

          <StyledTableContainer>
            <Table>
              <StyledTableHead>
                <StyledTableRow>
                  {/* Checkbox column header is empty or could have 'Select All' */}
                  <TableCell padding="checkbox" />
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {documentTypes.map((doc) => {
                  const isChecked = selectedDocIds.includes(doc.document_id);
                  return (
                    <StyledTableRow key={doc.document_id} onClick={() => handleToggleDoc(doc.document_id)} role="checkbox" sx={{ cursor: 'pointer' }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={isChecked} color="primary" />
                      </TableCell>
                      <TableCell>{doc.document_type}</TableCell>
                      <TableCell sx={{ color: '#666' }}>{doc.document_description}</TableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <Box sx={{ display: 'flow-root' }}> {/* Clear floats */}
            <SaveButton
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={isSaving || !selectedRoleId}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </SaveButton>
          </Box>
        </MappingPanel>
      </LayoutGrid>
    </MainContainer>
  );
};

export default RoleDocumentTypeMapping;
